import React, { useEffect, useMemo, useRef, useState } from "react";
import { Grid, Card, Tabs, Tab, Button } from "@mui/material";
import DeleteIcon from '@mui/icons-material/Delete';
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { pageHeaderStyles } from "utils/pageHeaderStyles";
import { buttonStyles } from "utils/buttonStyles";
import { tabStyles } from "utils/tabStyles";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "../../examples/Tables/DataTable";
import CustomPagination from "components/CustomPagination";
import TerimaanSearch from "../../components/STSearch/TerimaanSearch";
import { useSnackbar } from "components/GlobalSnackbar";
import { useConfirmDialog } from "components/ConfirmDialog";
import { handleApiError } from "utils/errorHandler";

import { apiSearchAkaunLikeAll } from "api/akaun";
// import { getUnpaidInboisByNoAkaun } from "api/inbois"; // unused


function TerimaanHasil() {
  const { showSuccess, showError, showInfo } = useSnackbar();
  const { showConfirm } = useConfirmDialog();
  // TambahTerimaan popup state
  const [openTambahTerimaan, setOpenTambahTerimaan] = useState(false);
  // Results and UI state
  const [akaunData, setAkaunData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedAkaun, setSelectedAkaun] = useState(null);
  // Handler for saving new terimaan (implement actual save logic as needed)
  const handleSaveTerimaan = async (data) => {
    // TODO: Save logic here
    setOpenTambahTerimaan(false);
  };
  // Search inputs
  const [searchNama, setSearchNama] = useState("");
  const [searchNoKp, setSearchNoKp] = useState("");
  const [noAkaun, setNoAkaun] = useState("");
  const debounceRef = useRef();
  const [activeTab, setActiveTab] = useState(0);
  const [terimaanData, setTerimaanData] = useState([]);
  const [lejarData, setLejarData] = useState([]);
  const [depositData, setDepositData] = useState([]);
  const [loadingTabs, setLoadingTabs] = useState(false);
  const [selectedTerimaanIds, setSelectedTerimaanIds] = useState([]); // multi-select for terimaan deletion
  const [isDeletingTerimaan, setIsDeletingTerimaan] = useState(false);

  // --- Sorting helpers to mirror HartaDetails backend lejar order ---
  const normalizePerkara = (s) => (s || "").toString().toLowerCase().trim().replace(/\s+/g, " ");
  const isBakiAwalTunggakan = (r) =>
    normalizePerkara(r?.perkara) === "baki awal tunggakan" &&
    Number(r?.debit || 0) === 0 &&
    Number(r?.kredit || 0) === 0;
  const parseDateSafe = (d) => (d ? new Date(d) : null);
  const terimaanComparator = (a, b) => {
    // 1) tarikh_resit ASC (fallbacks supported), NULLS LAST
    const aDate = parseDateSafe(a?.tarikh_resit || a?.tarikh || a?.created_at) || null;
    const bDate = parseDateSafe(b?.tarikh_resit || b?.tarikh || b?.created_at) || null;
    if (aDate && bDate) {
      const diff = aDate - bDate;
      if (diff !== 0) return diff;
    } else if (aDate && !bDate) {
      return -1;
    } else if (!aDate && bDate) {
      return 1;
    }
    // 2) If same date AND same no_resit, sort by Perkara ASC
    const aResit = (a?.no_resit ?? "").toString().trim();
    const bResit = (b?.no_resit ?? "").toString().trim();
    if (aResit && bResit && aResit === bResit) {
      const aPer = normalizePerkara(a?.perkara);
      const bPer = normalizePerkara(b?.perkara);
      const cmp = aPer.localeCompare(bPer, "ms", { sensitivity: "base", numeric: true });
      if (cmp !== 0) return cmp;
    }
    // 3) Final tie-breaker by id ASC
    const aId = Number(a?.id || 0);
    const bId = Number(b?.id || 0);
    return aId - bId;
  };
  const lejarComparator = (a, b) => {
    // 1) Special rows first
    const aAwal = isBakiAwalTunggakan(a) ? 0 : 1;
    const bAwal = isBakiAwalTunggakan(b) ? 0 : 1;
    if (aAwal !== bAwal) return aAwal - bAwal;
    // 2) tarikh_resit ASC, NULLS LAST
    const aDate = parseDateSafe(a?.tarikh_resit) || null;
    const bDate = parseDateSafe(b?.tarikh_resit) || null;
    if (aDate && bDate) {
      const diff = aDate - bDate;
      if (diff !== 0) return diff;
    } else if (aDate && !bDate) {
      return -1; // b is NULL, so a comes first
    } else if (!aDate && bDate) {
      return 1; // a is NULL, put after b
    }
    // 3) If same date AND same doc_no (no_resit), sort by Perkara ASC
    const aDoc = (a?.doc_no ?? "").toString().trim();
    const bDoc = (b?.doc_no ?? "").toString().trim();
    if (aDoc && bDoc && aDoc === bDoc) {
      const cmpPer = normalizePerkara(a?.perkara).localeCompare(normalizePerkara(b?.perkara), "ms", { sensitivity: "base", numeric: true });
      if (cmpPer !== 0) return cmpPer;
    }
    // 4) id ASC as tiebreaker
    const aId = Number(a?.id || 0);
    const bId = Number(b?.id || 0);
    return aId - bId;
  };

  // Debounced LIKE search against akaun (server should ignore bahagian for permitted users)
  useEffect(() => {
    const hasNama = !!(searchNama && searchNama.trim());
    const hasNoKp = !!(searchNoKp && searchNoKp.trim());
    const hasNoAkaun = !!(noAkaun && noAkaun.trim());

    if (!hasNama && !hasNoKp && !hasNoAkaun) {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = null;
      setAkaunData([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const params = {};
        if (hasNama) params.nama = searchNama.trim();
        if (hasNoKp) params.nokp = searchNoKp.replace(/\D/g, "");
        if (hasNoAkaun) params.no_akaun = noAkaun.trim();
  const result = await apiSearchAkaunLikeAll(params, 1, 10);
        setAkaunData(result.data || []);
      } catch (e) {
        setAkaunData([]);
      } finally {
        setLoading(false);
      }
    }, 450);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchNama, searchNoKp, noAkaun]);

  // Table definition mapping akaun data
  // Static table columns – memoized to keep stable references
  const columns = useMemo(() => [
    { Header: "", accessor: "select", width: "5%", align: "center", headerAlign: "center" },
    { Header: "No KP", accessor: "no_kp", width: "15%", align: "center", headerAlign: "center" },
    { Header: "No Akaun", accessor: "no_akaun", width: "15%", align: "center", headerAlign: "center" },
    { Header: "Kod Pelanggan", accessor: "kod_pelanggan", width: "12%", align: "center", headerAlign: "center" },
    { Header: "Nama", accessor: "nama", width: "20%", align: "left", headerAlign: "center" },
    { Header: "Alamat Harta", accessor: "alamat1_harta", width: "25%", align: "left", headerAlign: "center" },
    { Header: "Kategori Kutipan", accessor: "kategori_kutipan", width: "10%", align: "center", headerAlign: "center" },
    { Header: "Bahagian", accessor: "bahagian_pemantau", width: "10%", align: "center", headerAlign: "center" },
  ], []);

  // Rows depend on akaunData and selectedAkaun for the checkbox state
  const rows = useMemo(() => (akaunData || []).map((akaun) => {
    const noAkaunVal = akaun.noakaun || akaun.no_akaun || "-";
    const isSelected = selectedAkaun && (selectedAkaun.noakaun || selectedAkaun.no_akaun) === noAkaunVal;
    return {
      select: (
        <input
          type="checkbox"
          checked={!!isSelected}
          onChange={() => {
            if (isSelected) {
              setSelectedAkaun(null);
            } else {
              setSelectedAkaun(akaun);
              // Only clear inputs if they are non-empty to avoid redundant state updates
              setSearchNama((prev) => (prev ? "" : prev));
              setSearchNoKp((prev) => (prev ? "" : prev));
              setNoAkaun((prev) => (prev ? "" : prev));
              // Removed setAkaunData([akaun]) to prevent infinite update loop
            }
          }}
          style={{ cursor: "pointer" }}
        />
      ),
      _no_akaun_raw: noAkaunVal,
      no_kp: (
        <MDTypography variant="caption" color="text" fontWeight="medium">
          {akaun.no_kp || akaun.nokp || "-"}
        </MDTypography>
      ),
      no_akaun: (
        <MDTypography variant="caption" color="text" fontWeight="medium">
          {noAkaunVal}
        </MDTypography>
      ),
      kod_pelanggan: (
        <MDTypography variant="caption" color="text" fontWeight="medium">
          {akaun.kod_pelanggan || akaun.kodpelanggan || "-"}
        </MDTypography>
      ),
      nama: (
        <MDBox display="flex" alignItems="center" lineHeight={1}>
          <MDTypography variant="button" fontWeight="medium" ml={0.05} lineHeight={1}>
            {akaun.nama || "-"}
          </MDTypography>
        </MDBox>
      ),
      alamat1_harta: (
        <MDTypography variant="caption" color="text" fontWeight="medium">
          {akaun.alamat1_harta || "-"}
        </MDTypography>
      ),
      kategori_kutipan: (
        <MDTypography variant="caption" color="text" fontWeight="medium">
          {akaun.kategori_kutipan || akaun.kategorikutipan || "-"}
        </MDTypography>
      ),
      bahagian_pemantau: (
        <MDTypography variant="caption" color="text" fontWeight="medium">
          {akaun.bahagian_pemantau || akaun.bahagian || "-"}
        </MDTypography>
      ),
    };
  }), [akaunData, selectedAkaun]);

  const displayedRows = useMemo(() => (
    selectedAkaun
      ? rows.filter((r) => r._no_akaun_raw === (selectedAkaun.noakaun || selectedAkaun.no_akaun))
      : rows
  ), [rows, selectedAkaun]);

  // Load Terimaan/Lejar/Deposit when an akaun is selected
  useEffect(() => {
    let cancelled = false;
    if (!selectedAkaun) {
      setTerimaanData([]);
      setLejarData([]);
      setDepositData([]);
      // Reset active tab only if needed to avoid redundant updates
      setActiveTab((prev) => (prev === 0 ? prev : 0));
      setSelectedTerimaanIds([]);
      return () => {};
    }
    const noAkaunSel = selectedAkaun.noakaun || selectedAkaun.no_akaun;
    if (!noAkaunSel) return () => {};
    setLoadingTabs(true);
    (async () => {
      try {
        const api = await import("api/terimaan");
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const { hasPermission } = await import("utils/permissions");
        const canViewAllDepts = hasPermission(user, 'CROSS_DEPARTMENT_VIEW');
        const dept = selectedAkaun.bahagian_pemantau || selectedAkaun.bahagian;
        
        // Try department-filtered first; if empty, fallback to cross-department endpoints
        let tRes, lRes, dRes;
        try {
          if (canViewAllDepts) {
            // Users with cross-department view (ADMIN, KAUNTER) can see all data
            [tRes, lRes, dRes] = await Promise.all([
              api.getTerimaanByNoAkaunHuda(noAkaunSel),
              api.getLejarByNoAkaunForDept(noAkaunSel, dept),
              api.getDepositByNoAkaunForDept(noAkaunSel, dept),
            ]);
          } else {
            // Department staff see only their department's data
            [tRes, lRes, dRes] = await Promise.all([
              api.getTerimaanByNoAkaunForDept(noAkaunSel, dept),
              api.getLejarByNoAkaunForDept(noAkaunSel, dept),
              api.getDepositByNoAkaunForDept(noAkaunSel, dept),
            ]);
          }
        } catch (_) {
          // If department mismatch/404, fallback
          [tRes, lRes, dRes] = await Promise.all([
            canViewAllDepts ? api.getTerimaanByNoAkaunHuda(noAkaunSel) : api.getTerimaanByNoAkaunAll(noAkaunSel),
            api.getLejarByNoAkaunAll(noAkaunSel),
            api.getDepositByNoAkaunAll(noAkaunSel),
          ]);
        }
        if (cancelled) return;
  const tData = (tRes?.data || []).slice().sort(terimaanComparator);
  const lData = (lRes?.data || []).slice().sort(lejarComparator);
        const dData = (dRes?.data || []).slice().sort((a, b) => new Date(a.tarikh_resit || a.tarikh || a.created_at) - new Date(b.tarikh_resit || b.tarikh || b.created_at));
        setTerimaanData(tData);
        setLejarData(lData);
        setDepositData(dData);
      } catch (err) {
        if (!cancelled) {
          setTerimaanData([]);
          setLejarData([]);
          setDepositData([]);
        }
      } finally {
        if (!cancelled) setLoadingTabs(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedAkaun]);

  // Auto-refresh only the currently active tab's data while an account is selected.
  // Refresh interval (ms) can be tuned; 15000 = 15s.
  useEffect(() => {
    if (!selectedAkaun) return; // nothing to refresh
    let cancelled = false;
    let intervalId;
    const noAkaunSel = selectedAkaun.noakaun || selectedAkaun.no_akaun;
    if (!noAkaunSel) return;
    const dept = selectedAkaun.bahagian_pemantau || selectedAkaun.bahagian;
    const refresh = async () => {
      try {
  const api = await import('api/terimaan');
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isHuda = (user.id_pengguna || user.idPengguna || user.username || '').toLowerCase() === 'huda';
        if (cancelled) return;
        if (activeTab === 0) {
          // Terimaan
          let tRes;
          try { tRes = isHuda ? await api.getTerimaanByNoAkaunHuda(noAkaunSel) : await api.getTerimaanByNoAkaunForDept(noAkaunSel, dept); } catch { tRes = isHuda ? await api.getTerimaanByNoAkaunHuda(noAkaunSel) : await api.getTerimaanByNoAkaunAll(noAkaunSel); }
          if (cancelled) return;
          const tData = (tRes?.data || []).slice().sort(terimaanComparator);
          setTerimaanData(tData);
        } else if (activeTab === 1) {
          // Lejar
            let lRes;
            try { lRes = await api.getLejarByNoAkaunForDept(noAkaunSel, dept); } catch { lRes = await api.getLejarByNoAkaunAll(noAkaunSel); }
            if (cancelled) return;
            const lData = (lRes?.data || []).slice().sort(lejarComparator);
            setLejarData(lData);
        } else if (activeTab === 2) {
          // Deposit
            let dRes;
            try { dRes = await api.getDepositByNoAkaunForDept(noAkaunSel, dept); } catch { dRes = await api.getDepositByNoAkaunAll(noAkaunSel); }
            if (cancelled) return;
            const dData = (dRes?.data || []).slice().sort((a,b)=> new Date(a.tarikh_resit || a.tarikh || a.created_at) - new Date(b.tarikh_resit || b.tarikh || b.created_at));
            setDepositData(dData);
        }
      } catch (e) {
        if (!cancelled) {
          if (activeTab === 0) setTerimaanData([]);
          if (activeTab === 1) setLejarData([]);
          if (activeTab === 2) setDepositData([]);
        }
      }
    };
    // initial immediate refresh for active tab
    refresh();
    intervalId = setInterval(refresh, 15000);
    return () => { cancelled = true; clearInterval(intervalId); };
  }, [activeTab, selectedAkaun]);

  // Helpers and tab table mappings
  const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-CA") : "-");
  const fmtRM = (n) => {
    const v = parseFloat(n || 0);
    return Number.isNaN(v) ? "0.00" : v.toFixed(2);
  };
  // RM formatter with thousand separators (comma) for display like HartaDetails
  const fmtRMComma = (n) => {
    const v = parseFloat(n || 0);
    return Number.isNaN(v)
      ? "0.00"
      : v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const terimaanColumns = useMemo(() => [
    { Header: "", accessor: "select", width: "5%", align: "center", headerAlign: "center" },
    { Header: "No Akaun", accessor: "no_akaun", width: "15%", align: "center", headerAlign: "center" },    
    { Header: "Tarikh", accessor: "tarikh_resit", width: "10%", align: "center", headerAlign: "center" },
    { Header: "No Resit", accessor: "no_resit", width: "15%", align: "center", headerAlign: "center" },
    { Header: "No Inbois", accessor: "no_inbois", width: "15%", align: "center", headerAlign: "center" },
    { Header: "Perkara", accessor: "perkara", width: "25%", align: "left", headerAlign: "center" },
    { Header: "Jumlah", accessor: "jumlah", width: "10%", align: "right", headerAlign: "center" },
    { Header: "Saluran Bayaran", accessor: "saluran_bayaran", width: "10%", align: "center", headerAlign: "center" },
  ], []);
  const terimaanRows = useMemo(() => (terimaanData || []).map((r) => ({
    select: (
      <input
        type="checkbox"
        checked={selectedTerimaanIds.includes(r.id)}
        onChange={() => {
          setSelectedTerimaanIds((prev) => prev.includes(r.id) ? prev.filter(id => id !== r.id) : [...prev, r.id]);
        }}
        style={{ cursor: 'pointer' }}
      />
    ),
    no_akaun: r.no_akaun || "-",
    tarikh_resit: fmtDate(r.tarikh_resit || r.tarikh_simpan || r.tarikh || "-"),
    no_resit: r.no_resit || "-",
    no_inbois: r.no_inbois || "-",
    perkara: r.perkara || "-",
    jumlah: fmtRM(r.jumlah || r.kredit || r.debit || 0),
    saluran_bayaran: r.saluran_bayaran || "-",
  })), [terimaanData, selectedTerimaanIds]);

  const handleDeleteSelectedTerimaan = async () => {
    if (selectedTerimaanIds.length === 0) {
      showInfo('Sila pilih rekod terimaan untuk dihapus');
      return;
    }
    
    const confirmed = await showConfirm({
      title: `Padam ${selectedTerimaanIds.length} Rekod Terimaan`,
      message: `Adakah anda pasti untuk memadam ${selectedTerimaanIds.length} rekod terimaan? Lejar yang berkaitan juga akan dipadam.`,
      confirmText: 'Padam',
      type: 'error'
    });
    
    if (!confirmed) return;
    try {
      setIsDeletingTerimaan(true);
      const { apiDeleteTerimaan, getTerimaanByNoAkaunAll, getLejarByNoAkaunAll, getTerimaanByNoAkaunHuda } = await import('api/terimaan');
      await apiDeleteTerimaan(selectedTerimaanIds);
      // After deletion refresh both Terimaan & Lejar (lejar rows linked by no_resit were removed server-side)
      const noAkaunSel = selectedAkaun.noakaun || selectedAkaun.no_akaun;
      if (noAkaunSel) {
        try {
          const user = JSON.parse(localStorage.getItem('user') || '{}');
          const isHuda = (user.id_pengguna || user.idPengguna || user.username || '').toLowerCase() === 'huda';
          const [tRes, lRes] = await Promise.all([
            isHuda ? getTerimaanByNoAkaunHuda(noAkaunSel) : getTerimaanByNoAkaunAll(noAkaunSel),
            getLejarByNoAkaunAll(noAkaunSel),
          ]);
          setTerimaanData((tRes?.data || []).slice().sort(terimaanComparator));
          setLejarData((lRes?.data || []).slice().sort(lejarComparator));
        } catch (e) { /* ignore */ }
      }
      setSelectedTerimaanIds([]);
      showSuccess('Rekod terimaan berjaya dihapus');
    } catch (err) {
      console.error('Hapus terimaan gagal:', err);
      handleApiError(err, showError);
    } finally {
      setIsDeletingTerimaan(false);
    }
  };

  const lejarColumns = useMemo(() => [
    { Header: "No Akaun", accessor: "no_akaun", width: "15%", align: "center", headerAlign: "center" },
    // Display the same date used for sorting: tarikh_resit (fallback to tarikh_simpan)
    { Header: "Tarikh", accessor: "tarikh", width: "10%", align: "center", headerAlign: "center" },
    { Header: "No Dokumen ", accessor: "doc_no", width: "15%", align: "center", headerAlign: "center" },
    { Header: "Perkara", accessor: "perkara", width: "25%", align: "center", headerAlign: "center" },
    { Header: "Debit", accessor: "debit", width: "10%", align: "center", headerAlign: "center" },
    { Header: "Kredit", accessor: "kredit", width: "10%", align: "center", headerAlign: "center" },
    { Header: "Baki", accessor: "baki", width: "10%", align: "center", headerAlign: "center" },
  ], []);
  const lejarRows = useMemo(() => (lejarData || []).map((r) => ({
    no_akaun: r.no_akaun || "-",
    // Match display with sort: prefer tarikh_resit, else fallback to tarikh_simpan
    tarikh: fmtDate(r.tarikh_resit || r.tarikh_simpan || "-"),
    doc_no: r.doc_no || "-",
    perkara: (
      <MDTypography
        variant="caption"
        fontWeight={typeof r.perkara === 'string' && r.perkara.startsWith('INV:') ? 'bold' : 'regular'}
        sx={typeof r.perkara === 'string' && r.perkara.startsWith('INV:') ? { color: '#1565c0' } : undefined}
        title={typeof r.perkara === 'string' && r.perkara.startsWith('INV:') ? 'Rekod Inbois' : undefined}
      >
        {r.perkara || "-"}
      </MDTypography>
    ),
    debit: fmtRMComma(r.debit),
    kredit: fmtRMComma(r.kredit),
    baki: fmtRMComma(r.baki),
  })), [lejarData]);

  const depositColumns = useMemo(() => [
    { Header: "No Akaun", accessor: "no_akaun", width: "15%", align: "center", headerAlign: "center" },    
    { Header: "Tarikh", accessor: "tarikh_simpan", width: "10%", align: "center", headerAlign: "center" },
    { Header: "No Resit", accessor: "no_resit", width: "15%", align: "center", headerAlign: "center" },
    { Header: "Perkara", accessor: "perkara", width: "25%", align: "left", headerAlign: "center" },
    { Header: "Jumlah", accessor: "jumlah", width: "10%", align: "right", headerAlign: "center" },
  ], []);
  const depositRows = useMemo(() => (depositData || []).map((r) => ({
    no_akaun: r.no_akaun || "-",
    tarikh_simpan: fmtDate(r.tarikh_simpan || "-"),
    no_resit: r.no_resit || "-",
    perkara: r.perkara || "-",
    jumlah: fmtRM(r.jumlah || r.kredit || r.debit || 0),
  })), [depositData]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={2}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={2}
                px={2}
                variant="gradient"
                sx={pageHeaderStyles.report}
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  Terimaan Hasil
                </MDTypography>
              </MDBox>
              <MDBox>
                <TerimaanSearch
                  searchNama={searchNama}
                  onSearchNamaChange={(e) => {
                    const v = e.target.value;
                    setSearchNama(v);
                    // Only clear selection if one is currently set to avoid redundant updates
                    if (selectedAkaun) setSelectedAkaun(null);
                  }}
                  searchNoKp={searchNoKp}
                  onSearchNoKpChange={(e) => {
                    const v = e.target.value;
                    setSearchNoKp(v);
                    if (selectedAkaun) setSelectedAkaun(null);
                  }}
                  noAkaun={noAkaun}
                  onNoAkaunChange={(e) => {
                    const v = e.target.value;
                    setNoAkaun(v);
                    if (selectedAkaun) setSelectedAkaun(null);
                  }}
                  displayAkaunData={akaunData}
                  loading={loading}
                  selectedAkaun={selectedAkaun}
                  onSelectAkaun={setSelectedAkaun}
                />
                <DataTable
                  table={{ columns, rows: displayedRows }}
                  isSorted={false}
                  entriesPerPage={false}
                  showTotalEntries={false}
                  noEndBorder
                  canSearch={false}
                />

                {selectedAkaun && (
                  <MDBox mt={3}>
                    <MDBox display="flex" flexDirection="column" mb={1} ml={3} mr={3} gap={1}>
                      <MDTypography variant="h6">
                        Maklumat Akaun: {selectedAkaun.noakaun || selectedAkaun.no_akaun} – {selectedAkaun.nama || ""}
                      </MDTypography>
                      <MDBox display="flex" alignItems="center" justifyContent="space-between" gap={2}>
                        <Button
                          variant="contained"
                          size="small"
                          sx={buttonStyles.save}
                          onClick={() => setOpenTambahTerimaan(true)}
                        >
                          Terimaan Baru
                        </Button>
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<DeleteIcon />}
                          disabled={selectedTerimaanIds.length === 0 || isDeletingTerimaan}
                          onClick={handleDeleteSelectedTerimaan}
                          sx={buttonStyles.delete}
                        >
                          Hapus ({selectedTerimaanIds.length})
                        </Button>
                      </MDBox>
                    </MDBox>

                {/* TambahTerimaan popup */}
                <React.Suspense fallback={null}>
                  {openTambahTerimaan && (
                    (() => {
                      const TambahTerimaan = require("components/STPopup/TambahTerimaan").default;
                      return (
                        <TambahTerimaan
                          open={openTambahTerimaan}
                          onClose={() => setOpenTambahTerimaan(false)}
                          onSave={handleSaveTerimaan}
                          defaultNoAkaun={selectedAkaun.noakaun || selectedAkaun.no_akaun}
                        />
                      );
                    })()
                  )}
                </React.Suspense>
                    <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} aria-label="akaun-tabs" sx={{ ...tabStyles.filled, mx: 3 }}>
                      <Tab label="Terimaan" />
                      <Tab label="Lejar" />
                      <Tab label="Deposit" />
                    </Tabs>
                    <MDBox
                      mt={2}
                      sx={{
                        // Hide any built-in pagination inside the tab tables only (Terimaan/Lejar/Deposit)
                        '& .MuiTablePagination-root, & .MuiTablePagination-toolbar, & .MuiTableFooter-root, & .MuiPagination-root, & .MuiPagination-ul, & nav[aria-label="pagination navigation"]':
                          { display: 'none !important' },
                        // Match HartaDetails table body color (lighter gray)
                        '& table tbody td, & table tbody td *': { color: 'rgba(0,0,0,0.6) !important' },
                      }}
                    >
                      {loadingTabs ? (
                        <MDTypography variant="button">Memuatkan data…</MDTypography>
                      ) : (
                        <>
                          {activeTab === 0 && (
                            <DataTable
                              table={{ columns: terimaanColumns, rows: terimaanRows }}
                              isSorted={false}
                              entriesPerPage={false}
                              showTotalEntries={false}
                              noEndBorder
                              canSearch={false}
                              noPagination
                              fontSize="0.825rem"
                            />
                          )}
                          {activeTab === 1 && (
                            <DataTable
                              table={{ columns: lejarColumns, rows: lejarRows }}
                              isSorted={false}
                              entriesPerPage={false}
                              showTotalEntries={false}
                              noEndBorder
                              canSearch={false}
                              noPagination
                              fontSize="0.825rem"
                            />
                          )}
                          {activeTab === 2 && (
                            <DataTable
                              table={{ columns: depositColumns, rows: depositRows }}
                              isSorted={false}
                              entriesPerPage={false}
                              showTotalEntries={false}
                              noEndBorder
                              canSearch={false}
                              noPagination
                              fontSize="0.825rem"
                            />
                          )}
                        </>
                      )}
                    </MDBox>
                  </MDBox>
                )}

                {/* Pagination only for akaun search list; hidden when viewing tabs */}
                  {/* Show pagination only for the akaun list (hidden when tabs are shown) */}
                  {!selectedAkaun && (
                    <CustomPagination
                      currentPage={1}
                      totalPages={1}
                      totalItems={(akaunData || []).length}
                      itemsPerPage={10}
                      actualItemsOnPage={(akaunData || []).length}
                      onPageChange={() => {}}
                      onNextPage={() => {}}
                      onPrevPage={() => {}}
                      variant="contained"
                      color="info"
                    />
                  )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
}

export default TerimaanHasil;
